import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, Phone, MapPin } from 'lucide-react';
import { services, serviceAreas } from '../mockData';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_area: '',
    service_interest: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Submit to backend
      const response = await axios.post(`${API}/contact`, formData);

      console.log('Contact form submitted:', response.data);

      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you shortly."
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_area: '',
        service_interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error sending your message. Please try again or call us directly.",
        variant: "destructive"
      });
    }
  };

  return (
    <section id="booking" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Request a Quote
          </h2>
          <p className="text-xl text-gray-600">
            Get in touch for a free, no-obligation estimate
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-2 border-green-100">
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Phone className="h-6 w-6 text-green-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Phone</p>
                      <a href="tel:236-268-0552" className="text-green-700 hover:text-green-800 transition-colors">
                        (236) 268-0552
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Mail className="h-6 w-6 text-green-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Email</p>
                      <a href="mailto:info@vancouverhousewash.com" className="text-green-700 hover:text-green-800 transition-colors break-all">vanhousewash@gmail.com

                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl border-2 border-green-100">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
                <CardTitle className="text-2xl">Request a Free Quote</CardTitle>
                <CardDescription className="text-base">Fill out the form below and we'll respond within 24 hours</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-name" className="text-gray-700 font-medium">Name *</Label>
                      <Input
                        id="contact-name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500" />

                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone" className="text-gray-700 font-medium">Phone *</Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(236) 268-0552"
                        className="border-gray-300 focus:border-green-500 focus:ring-green-500" />

                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-gray-700 font-medium">Email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="border-gray-300 focus:border-green-500 focus:ring-green-500" />

                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact-area" className="text-gray-700 font-medium">Service Area *</Label>
                      <Select value={formData.service_area} onValueChange={(value) => setFormData({ ...formData, service_area: value })}>
                        <SelectTrigger className="border-gray-300 focus:border-green-500 focus:ring-green-500">
                          <SelectValue placeholder="Select your area" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceAreas.map((area, index) =>
                          <SelectItem key={index} value={area}>{area}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-service" className="text-gray-700 font-medium">Service Interest *</Label>
                      <Select value={formData.service_interest} onValueChange={(value) => setFormData({ ...formData, service_interest: value })}>
                        <SelectTrigger className="border-gray-300 focus:border-green-500 focus:ring-green-500">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) =>
                          <SelectItem key={service.id} value={service.title}>{service.title}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message" className="text-gray-700 font-medium">Message</Label>
                    <Textarea
                      id="contact-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your project or ask any questions..."
                      rows={5}
                      className="border-gray-300 focus:border-green-500 focus:ring-green-500" />

                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-green-700 hover:bg-green-800 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all">

                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>);

};

export default ContactForm;